import { IBasePicker, ITag } from '@fluentui/react';
import { IPDefaultButton, IPLink, IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import EditTextFieldCallOut from 'components/CallOuts/EditTextFieldCallOut';
import SingleTagPicker from 'components/Pickers/SingleTagPicker';
import CategoryChips from 'components/Tags/CategoryChips';
import ColorField, { DEFAULT_TAG_COLOR } from 'components/Tags/ColorField';
import {
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  globalTextStylesValidationError,
} from 'globalStyles';
import Tag, { TagSystemTypes } from 'models/tag';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISingleTag } from './SingleTag';

let newTagId: number = -1;

interface ITagCreatorProps {
  tagCache: Tag[];
  onClose: () => void;
  onCreate: (tag: Tag) => void;
  saveButtonText: string;
  defaultGroup?: string | undefined;
  showCatagory?: boolean;
  // Panel mode: the host (TagFormPanel) renders Save/Cancel in the panel footer, so hide the inline
  // buttons and expose the save action + disabled state via these callbacks.
  hideButtons?: boolean;
  onSaveCallback?: (save: () => void) => void;
  onSaveDisabledCallback?: (isDisabled: () => boolean) => void;
}

const TagCreator = (props: ITagCreatorProps) => {
  const { t } = useTranslation(['translation', 'tag']);
  const [tagKeys, setTagKeys] = useState<ISingleTag[]>([]);
  const [tagValues, setTagValues] = useState<ISingleTag[]>([]);
  const [selectedKey, setSelectedKey] = useState<ISingleTag | undefined>(undefined);
  const [selectedValue, setSelectedValue] = useState<ISingleTag | undefined>(undefined);
  const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_TAG_COLOR);
  const [selectedInfo, setSelectedInfo] = useState<string | undefined>(undefined);
  // Defaults to "None" so the Category reads as optional (the field is never in an unanswered state).
  const [selectedSystemType, setSelectedSystemType] = useState<number | undefined>(TagSystemTypes.None);
  const [editTagInfoCallout, setEditTagInfoCallout] = useState<boolean>(false);
  const [isNewKey, setIsNewKey] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const groupPickerRef = createRef<IBasePicker<ITag>>();
  const valuePickerRef = createRef<IBasePicker<ITag>>();

  const calloutId = useMemo(() => 'tag-creator-' + Math.random().toString(36).substring(7), []);

  useEffect(() => {
    const loadData = async () => {
      const keys: ISingleTag[] = [];

      props.tagCache.forEach((t) => {
        if (keys.findIndex((k) => k.tagName === t.tagName) === -1) {
          keys.push({ tagId: t.tagName, tagName: t.tagName, tagColor: t.tagColor });
        }
      });

      setTagKeys(keys);
    };

    loadData();

    if (!props.defaultGroup) {
      groupPickerRef.current?.focus();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tagCache]);

  useEffect(() => {
    if (selectedKey === undefined) {
      setIsNewKey(false);
    }

    if (selectedKey !== undefined && selectedValue !== undefined) {
      const exists = props.tagCache.some(
        (t) =>
          t.tagName.toLowerCase() === selectedKey.tagName.toLowerCase() &&
          t.tagValue.toLowerCase() === selectedValue.tagName.toLowerCase(),
      );
      if (exists) {
        setError(t('tag:Creator.NotUnique'));

        return;
      }
    }

    if (selectedKey && selectedKey.tagName.length > 32) {
      setError(t('tag:Creator.TooLong', { len: 32 }));

      return;
    }

    if (selectedValue && selectedValue.tagName.length > 64) {
      setError(t('tag:Creator.TooLong', { len: 64 }));

      return;
    }

    setError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, selectedValue]);

  const setTagKey = (tag: ISingleTag, isNew: boolean) => {
    const values: ISingleTag[] = [];

    props.tagCache.forEach((t) => {
      if (
        t.tagName.toLowerCase() === tag.tagName.toLowerCase() &&
        values.findIndex((k) => k.tagName.toLowerCase() === t.tagValue.toLowerCase()) === -1
      ) {
        values.push({ tagId: t.tagValue, tagName: t.tagValue, tagColor: t.tagColor });
      }
    });

    setTagValues(values);
    setSelectedKey(tag);
    setIsNewKey(isNew);
    setSelectedColor(tag.tagColor || DEFAULT_TAG_COLOR);

    valuePickerRef.current?.focus();
  };

  const setTagValue = (tag: ISingleTag) => {
    setSelectedValue(tag);
  };

  const getSelectedTag = (): Tag | undefined => {
    if (selectedKey === undefined || selectedValue === undefined) return;

    const newTag: Tag = new Tag();
    newTag.tagId = newTagId;
    newTag.tagName = selectedKey.tagName;
    newTag.tagValue = selectedValue.tagName;
    newTag.tagColor = selectedColor || DEFAULT_TAG_COLOR;
    newTag.tagInfo = selectedInfo;
    newTag.tagSystemType = selectedSystemType;

    newTagId = newTagId - 1;

    return newTag;
  };

  const isSaveDisabled = (): boolean => error !== undefined || selectedKey === undefined || selectedValue === undefined;

  const doSave = () => {
    const newTag = getSelectedTag();
    if (newTag) {
      props.onCreate(newTag);
      props.onClose();
    }
  };

  // Panel mode: the host (TagFormPanel) renders Save in its footer and reads our disabled state through
  // the registered getter at *its own* render time. The refs keep that getter reading live values, but
  // the host only re-renders when we re-register -- so we must re-register whenever validity flips
  // (selectedKey/selectedValue/error). Without that, Save stays stuck at its initial (disabled) state.
  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;
  const isSaveDisabledRef = useRef(isSaveDisabled);
  isSaveDisabledRef.current = isSaveDisabled;

  useEffect(() => {
    props.onSaveCallback?.(() => doSaveRef.current());
    props.onSaveDisabledCallback?.(() => isSaveDisabledRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, selectedValue, error]);

  // The new group's color also tints its preview badge in the Group/Value pickers. Replace the selected
  // items with new objects rather than mutating them, so React actually re-renders the badges.
  const onColorChange = (color: string) => {
    setSelectedColor(color);
    if (!isNewKey) return;
    if (selectedKey) setSelectedKey({ ...selectedKey, tagColor: color });
    if (selectedValue) setSelectedValue({ ...selectedValue, tagColor: color });
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPText>{t('tag:Creator.Explaination')}</IPText>
      </IPStack.Item>
      <IPStack horizontal wrap tokens={globalStackTokensGapSmall}>
        <IPStack.Item grow>
          <IPText styles={globalTextStylesBold}>{t('tag:Creator.Group')}</IPText>
          <SingleTagPicker
            ref={groupPickerRef}
            tags={tagKeys}
            itemLimit={1}
            selectedTags={selectedKey ? [selectedKey] : undefined}
            onAdd={setTagKey}
            onRemove={() => setSelectedKey(undefined)}
            colorForNewItems={selectedColor || DEFAULT_TAG_COLOR}
            preventTabKey={true}
            defaultValue={props.defaultGroup?.split(':', 2)[0].trimStart().trimEnd()}
            placeholder={t('tag:Picker.PlaceholderCreateGroup')}
            createNewLabel={t('tag:Picker.CreateNewGroup')}
          ></SingleTagPicker>
        </IPStack.Item>
        <IPStack.Item grow>
          <IPText styles={globalTextStylesBold}>{t('tag:Creator.Value')}</IPText>
          <SingleTagPicker
            ref={valuePickerRef}
            tags={tagValues}
            itemLimit={1}
            selectedTags={selectedValue ? [selectedValue] : undefined}
            onAdd={setTagValue}
            onRemove={() => setSelectedValue(undefined)}
            colorForNewItems={selectedColor || DEFAULT_TAG_COLOR}
            preventTabKey={isNewKey}
            defaultValue={props.defaultGroup?.split(':', 2)[1]?.trimStart()}
            placeholder={t('tag:Picker.PlaceholderCreateValue')}
            createNewLabel={t('tag:Picker.CreateNewValue')}
          ></SingleTagPicker>
        </IPStack.Item>
      </IPStack>
      {error && (
        <IPStack.Item>
          <IPText styles={globalTextStylesValidationError} variant="small">
            {error}
          </IPText>
        </IPStack.Item>
      )}
      {/* A new group needs its own badge color; an existing group inherits its color, so the picker is
          only shown while creating a new group. */}
      {isNewKey && (
        <IPStack.Item>
          <ColorField color={selectedColor} onChange={onColorChange} />
        </IPStack.Item>
      )}
      <IPStack.Item grow={props.showCatagory ? 0 : 1}>
        <IPLink id={calloutId} onClick={() => setEditTagInfoCallout(true)}>
          {t('tag:Creator.Info.Label')}
        </IPLink>
        <EditTextFieldCallOut
          value={selectedInfo}
          targetId={calloutId}
          onClose={() => setEditTagInfoCallout(false)}
          onUpdate={setSelectedInfo}
          isVisible={editTagInfoCallout}
          maxLenght={512}
        />
      </IPStack.Item>
      {props.showCatagory && (
        <IPStack.Item grow>
          <CategoryChips selected={selectedSystemType} onChange={(st) => setSelectedSystemType(st)} />
        </IPStack.Item>
      )}
      {!props.hideButtons && (
        <IPStack.Item>
          <IPStack horizontal tokens={globalStackTokensGapSmall} horizontalAlign="end">
            <IPStack.Item>
              <IPPrimaryButton onClick={doSave} disabled={isSaveDisabled()}>
                {props.saveButtonText}
              </IPPrimaryButton>
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton onClick={() => props.onClose()}>
                {t('translation:General.Button.Cancel')}
              </IPDefaultButton>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default TagCreator;
